import {
  CircularProgress,
  FormControl,
  FormControlProps,
  FormHelperText,
  InputLabel,
  ListItem,
  MenuItem,
  Select,
} from '@mui/material';
import { SelectProps } from '@mui/material/Select/Select';
import { useMemo } from 'react';
import { v4 as uuidV4 } from 'uuid';

import { useEmployeeSelect } from '~/components/GenericForm/EmployeeSelect/useEmployeeSelect';
import { getLinkedInputLabelAndSelectProps } from '~/components/GenericForm/EnumSelect/data';
import { EmployeeItemInfo } from '~/components/GenericList/EmployeeItemInfo/component';

export type EmployeeSelectProps = SelectProps & {
  FormControlProps?: FormControlProps;
  optionEmployeeIds: number[];
  helperText?: string;
  isLoading?: boolean;
};

export const EmployeeSelect = (props: EmployeeSelectProps) => {
  const {
    FormControlProps,
    optionEmployeeIds,
    children,
    helperText,
    isLoading,
    ...selectProps
  } = props;

  const { label, required, id, size, value } = selectProps;
  const linkedComponentProps = getLinkedInputLabelAndSelectProps({
    label,
    selectId: id || uuidV4(),
    required,
    ...(size === 'small' && { size: 'small' }),
  });

  const selectedEmployeeId = useMemo(
    () => (!!value ? parseInt(value as string) : undefined),
    [value],
  );

  const { options, employees } = useEmployeeSelect({
    employeeIds: optionEmployeeIds,
    selectedEmployeeId,
  });

  const resolvedSelectProps = {
    ...selectProps,
    ...linkedComponentProps.selectProps,
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <FormControl {...FormControlProps}>
      <InputLabel {...linkedComponentProps.inputLabelProps} />
      <Select
        {...resolvedSelectProps}
        data-testid="employee-select-dropdown"
        renderValue={(value) => {
          const info = employees.find(
            (employee) => employee.employeeId === value,
          );

          return (
            <ListItem sx={{ padding: 0 }} component={'div'}>
              {!!info && <EmployeeItemInfo employeeInfo={info} />}
            </ListItem>
          );
        }}
      >
        {children}
        {!!options &&
          options.map((option) => (
            <MenuItem
              key={option.employeeId}
              value={option.employeeId}
              data-testid={`employee-select-option-${option.employeeId}`}
            >
              <EmployeeItemInfo employeeInfo={option} />
            </MenuItem>
          ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};
